import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LocationOn, Phone, ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { Loader } from '@googlemaps/js-api-loader';

// Declare global google object
declare global {
  interface Window {
    google: typeof google;
  }
}

const RegisterContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#07090e',
  backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)',
  padding: '20px',
}));

const RegisterPaper = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '24px',
  maxWidth: '450px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '30px',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: theme.palette.background.default,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.disabled,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: theme.palette.text.primary,
  },
}));

const NextButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '10px',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: theme.palette.common.white,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  fontSize: '16px',
  fontWeight: 'bold',
  border: `2px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface Step1Data {
  companyName: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface AddressData {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phoneNumber: string;
}

const RegisterStep2: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  
  const step1Data = location.state as Step1Data;
  
  const [formData, setFormData] = useState<AddressData>({
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    phoneNumber: '',
  });
  
  const [error, setError] = useState('');
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
          version: 'weekly',
          libraries: ['places'],
        });
        
        await loader.load();
        setIsGoogleMapsLoaded(true);
      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    loadGoogleMaps();
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (isGoogleMapsLoaded && autocompleteInputRef.current && !autocompleteRef.current && window.google) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        autocompleteInputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'us' },
        }
      );

      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
    }
  }, [isGoogleMapsLoaded]);

  // Redirect to step 1 if no data from previous step
  useEffect(() => {
    if (!step1Data) {
      navigate('/register');
    }
  }, [step1Data, navigate]);

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.address_components) return;

    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let country = '';
    let zipCode = '';

    place.address_components.forEach((component: any) => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (types.includes('route')) {
        route = component.long_name;
      } else if (types.includes('locality')) {
        city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      } else if (types.includes('country')) {
        country = component.long_name;
      } else if (types.includes('postal_code')) {
        zipCode = component.long_name;
      }
    });

    const fullAddress = `${streetNumber} ${route}`.trim();

    setFormData(prev => ({
      ...prev,
      streetAddress1: fullAddress,
      city,
      state,
      country,
      zipCode,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation - all fields required except streetAddress2
    if (!formData.streetAddress1 || !formData.city || !formData.state || !formData.country || !formData.zipCode || !formData.phoneNumber) {
      setError('Please fill in all required fields');
      return;
    }

    // Combine step 1 and step 2 data
    const combinedData = {
      ...step1Data,
      ...formData,
    };

    // Navigate to Step 3 with combined data
    navigate('/register/step3', { state: combinedData });
  };

  const handleBack = () => {
    navigate('/register', { state: step1Data });
  };

  if (!step1Data) {
    return null; // Will redirect in useEffect
  }

  return (
    <RegisterContainer>
      <RegisterPaper elevation={8}>
        <form onSubmit={handleSubmit}>
          <LogoContainer>
            <Box
              component="img"
              src="/SkimBusterHeaderLogin-21.svg"
              alt="SkimBuster by MICCLO"
              sx={{
                height: '100px',
                objectFit: 'contain',
                filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1) brightness(0.9)' : 'none'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                marginTop: 2,
                fontWeight: 'bold',
                color: muiTheme.palette.text.primary,
              }}
            >
              Address Information
            </Typography>
            <Typography
              variant="body2"
              sx={{
                marginTop: 1,
                color: muiTheme.palette.text.secondary,
                textAlign: 'center',
              }}
            >
              Step 2 of 4
            </Typography>
          </LogoContainer>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2, borderRadius: '16px' }}>
              {error}
            </Alert>
          )}

          <StyledTextField
            fullWidth
            label="Street Address 1"
            name="streetAddress1"
            value={formData.streetAddress1}
            onChange={handleChange}
            required
            placeholder="Start typing your address..."
            inputRef={autocompleteInputRef}
            helperText={isGoogleMapsLoaded ? "Start typing to see address suggestions" : "Loading address suggestions..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Street Address 2 (Optional)"
            name="streetAddress2"
            value={formData.streetAddress2}
            onChange={handleChange}
            placeholder="Apartment, suite, etc."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, marginBottom: '20px' }}>
            <StyledTextField
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn sx={{ color: muiTheme.palette.text.disabled }} />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              fullWidth
              label="Zip Code"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              required
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn sx={{ color: muiTheme.palette.text.disabled }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <StyledTextField
            fullWidth
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Phone Number"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
            type="tel"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <NextButton type="submit" variant="contained">
            Next Step
          </NextButton>

          <BackButton
            type="button"
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </BackButton>
        </form>
      </RegisterPaper>
    </RegisterContainer>
  );
};

export default RegisterStep2;